import React, { useEffect, useState, useContext } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, ArrowRight, Star } from 'lucide-react';
import axios from 'axios';
import { AuthContext } from '../App';
import FavoriteButton from '../components/FavoriteButton';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { user, setShowAuthModal } = useContext(AuthContext);
  const [featuredTutorials, setFeaturedTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    fetchTutorials();
  }, []);

  const fetchTutorials = async () => {
    try {
      const response = await axios.get(`${API}/tutorials`);
      setFeaturedTutorials(response.data.slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch tutorials', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pb-24 md:pb-12"
    >
      {/* Hero Section - Magazine Style */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-5 space-y-8">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <p className="font-accent text-sm uppercase tracking-wider text-primary mb-4">Premium Beauty Tutorials</p>
                <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-charcoal leading-none tracking-tighter mb-6">
                  Révélez votre
                  <span className="block mt-2 text-primary">Beauté</span>
                </h1>
                <p className="text-lg text-taupe leading-relaxed max-w-lg">
                  Découvrez des tutoriels exclusifs de maquillage et de coiffure, créés par des experts. 
                  Nouvelles vidéos chaque semaine.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                {user && user.has_subscription ? (
                  <Link
                    to="/browse"
                    data-testid="hero-browse-btn"
                    className="inline-flex items-center justify-center bg-primary text-white hover:bg-primary/90 px-8 py-4 uppercase tracking-widest font-serif text-sm transition-all duration-500 group"
                  >
                    Explorer les tutoriels
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                ) : (
                  <button
                    onClick={() => setShowAuthModal(true)}
                    data-testid="hero-subscribe-btn"
                    className="inline-flex items-center justify-center bg-secondary text-white hover:bg-secondary/90 px-8 py-4 uppercase tracking-widest font-serif text-sm transition-all duration-500 shadow-xl"
                  >
                    <Star className="mr-2 w-5 h-5" />
                    Abonnez-vous - 9,29$
                  </button>
                )}
                <Link
                  to="/browse"
                  data-testid="hero-preview-btn"
                  className="inline-flex items-center justify-center bg-transparent border gold-border text-charcoal hover:bg-primary hover:text-white px-8 py-4 uppercase tracking-widest font-serif text-sm transition-all duration-500"
                >
                  Voir aperçu
                </Link>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="flex gap-12 pt-8 border-t gold-border"
              >
                <div>
                  <p className="font-serif text-3xl text-primary">100+</p>
                  <p className="text-sm text-taupe uppercase tracking-wide">Tutoriels</p>
                </div>
                <div>
                  <p className="font-serif text-3xl text-primary">$9.29</p>
                  <p className="text-sm text-taupe uppercase tracking-wide">À vie</p>
                </div>
                <div>
                  <p className="font-serif text-3xl text-primary">∞</p>
                  <p className="text-sm text-taupe uppercase tracking-wide">Nouveau contenu</p>
                </div>
              </motion.div>
            </div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-7"
            >
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-full h-full border gold-border"></div>
                <img
                  src="https://images.unsplash.com/photo-1765334666980-ccaf4b0a047a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxnbGFtb3JvdXMlMjBtYWtldXAlMjBtb2RlbCUyMGhpZ2glMjBmYXNoaW9ufGVufDB8fHx8MTc3MzQxNDQ5OXww&ixlib=rb-4.1.0&q=85"
                  alt="Glamourous Beauty"
                  className="relative w-full h-[600px] object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Tutorials */}
      <section className="py-20 md:py-32" data-testid="featured-section">
        <div className="container mx-auto px-6">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="font-accent text-sm uppercase tracking-wider text-primary mb-2">Nos Favoris</p>
              <h2 className="font-serif text-4xl md:text-5xl text-charcoal tracking-tighter">Tutoriels Populaires</h2>
            </div>
            <Link
              to="/browse"
              data-testid="featured-view-all-btn"
              className="hidden md:flex items-center text-primary hover:text-primary/80 uppercase tracking-wide text-sm transition-colors"
            >
              Voir tout
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-silk h-96 mb-4"></div>
                  <div className="bg-silk h-6 w-3/4 mb-2"></div>
                  <div className="bg-silk h-4 w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              {featuredTutorials.map((tutorial, index) => {
                const isLarge = index === 0;
                return (
                  <motion.div
                    key={tutorial.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`${
                      isLarge ? 'md:col-span-8' : 'md:col-span-4'
                    } group cursor-pointer relative`}
                  >
                    <Link to={`/tutorial/${tutorial.id}`} data-testid={`tutorial-card-${tutorial.id}`}>
                      <div className="overflow-hidden border gold-border mb-4">
                        <div className="relative">
                  <img
                    src={tutorial.thumbnail}
                    alt={tutorial.title}
                    className={`w-full object-cover image-zoom ${
                      isLarge ? 'h-96' : 'h-80'
                    }`}
                  />
                  <div className="absolute inset-0 cinema-fade opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                    <div className="w-16 h-16 bg-primary/90 flex items-center justify-center">
                      <Play className="w-8 h-8 text-white" fill="white" />
                    </div>
                  </div>
                          {tutorial.is_premium && (
                            <div className="absolute top-4 right-4 bg-primary px-3 py-1">
                              <span className="font-accent text-xs text-white uppercase tracking-wider">Premium</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="font-accent text-xs uppercase tracking-wider text-primary mb-2">{tutorial.category}</p>
                      <h3 className={`font-serif text-charcoal tracking-tighter group-hover:text-primary transition-colors ${
                        isLarge ? 'text-3xl' : 'text-2xl'
                      }`}>
                        {tutorial.title}
                      </h3>
                    </Link>
                    
                    <div className="absolute top-4 left-4 z-10">
                      <FavoriteButton tutorialId={tutorial.id} />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      {(!user || !user.has_subscription) && (
        <section className="py-20 md:py-32 bg-silk" data-testid="cta-section">
          <div className="container mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto space-y-8"
            >
              <p className="font-accent text-sm uppercase tracking-wider text-primary">Un Investissement</p>
              <h2 className="font-serif text-5xl md:text-6xl text-charcoal tracking-tighter leading-tight">
                Accès illimité à vie
                <span className="block mt-2">pour seulement 9,29$</span>
              </h2>
              <p className="text-lg text-taupe max-w-2xl mx-auto">
                Rejoignez des milliers de femmes qui ont transformé leur routine beauté. 
                Un seul paiement, un accès à vie à tout notre contenu.
              </p>
              <button
                onClick={() => setShowAuthModal(true)}
                data-testid="cta-subscribe-btn"
                className="inline-flex items-center bg-secondary text-white hover:bg-secondary/90 px-12 py-6 uppercase tracking-widest font-serif text-base transition-all duration-500 shadow-xl"
              >
                <Star className="mr-2 w-5 h-5" />
                Commencer maintenant
              </button>
            </motion.div>
          </div>
        </section>
      )}
    </motion.div>
  );
};

export default HomePage;